import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SimNotice } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/FormBits";
import AuthShell from "@/components/AuthShell";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [resetId, setResetId] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try { const r = await api.post("/auth/forgot", { identifier }); setResetId(r.data.reset_id); toast.success("Check your email for a reset code"); }
    catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setError("Passwords do not match");
    setBusy(true); setError("");
    try { await api.post("/auth/reset", { reset_id: resetId, code, new_password: pw.a }); toast.success("Password updated. Please sign in."); nav("/login"); }
    catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  return (
    <AuthShell>
      <div className="h-12 w-12 rounded-full bg-blue-50 text-[var(--blue)] grid place-items-center mb-4"><KeyRound /></div>
      <h1 className="text-3xl font-extrabold text-[var(--navy)]">Reset your password</h1>
      {!resetId ? (
        <form onSubmit={request} className="grid gap-5 mt-6" data-testid="forgot-form">
          <p className="text-slate-500 -mt-3">Enter your username or the email on your account. We'll email you a 6-digit code.</p>
          <Field label="Username or email"><Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required data-testid="forgot-identifier-input" /></Field>
          {error && <div className="text-sm text-[var(--red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="forgot-error">{error}</div>}
          <Button disabled={busy} className="h-11 rounded-full bg-[var(--blue)] hover:bg-[var(--navy)]" data-testid="forgot-submit-button">{busy ? "Sending..." : "Send reset code"}</Button>
        </form>
      ) : (
        <form onSubmit={reset} className="grid gap-5 mt-6" data-testid="reset-form">
          <p className="text-slate-500 -mt-3" data-testid="reset-sent-message">If an account matches, a code has been sent to the email on file. It expires in 15 minutes.</p>
          <Field label="6-digit code"><Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" className="h-12 text-xl tracking-[0.4em] text-center" required data-testid="reset-code-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="New password"><Input type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} required autoComplete="new-password" data-testid="reset-password-input" /></Field>
            <Field label="Confirm new password"><Input type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} required autoComplete="new-password" data-testid="reset-confirm-input" /></Field>
          </div>
          {error && <div className="text-sm text-[var(--red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="reset-error">{error}</div>}
          <Button disabled={busy || code.length !== 6} className="h-11 rounded-full bg-[var(--blue)] hover:bg-[var(--navy)]" data-testid="reset-submit-button">{busy ? "Updating..." : "Update password"}</Button>
          <button type="button" onClick={request} className="text-sm font-semibold text-[var(--blue)]" data-testid="reset-resend-button">Send a new code</button>
        </form>
      )}
      <p className="text-sm text-slate-500 text-center mt-6"><Link to="/login" className="font-semibold text-[var(--blue)]" data-testid="forgot-back-to-login">Back to sign in</Link></p>
      <SimNotice className="mt-8" />
    </AuthShell>
  );
}
